import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { TooltipProvider } from '@velora/ui';
import { ApiError } from '@velora/sdk';
import { useEffect, useState } from 'react';
import {
  identityKeys,
  SessionProvider as IdentitySessionProvider,
  useSessionContext,
} from '../modules/identity/index.js';
import { isPlaceholder, withBuiltins } from './builtin/modules.js';
import { installDemoIdentityFetch } from './demo/mode.js';
import { discoverWebModules } from './modules.js';
import { PrefsProvider } from './prefs/prefs.js';
import { PrivacyProvider } from './privacy/privacy.js';
import { purgeOfflineCaches } from './pwa/runtime.js';
import { buildRouter } from './router.js';
import { snapshotRetry, snapshotRetryDelay } from './snapshot.js';

/** Discovered modules plus the shell's Home dashboard and placeholders. */
export const shellModules = withBuiltins(discoverWebModules());

installDemoIdentityFetch();

/**
 * A 401 on any non-identity query means the session ended, locked or needs
 * a second step since it was last read: re-read it so the sign-in gate
 * shows the right screen (docs/requests/wp0-auth-shell-integration.md, 7).
 */
export function isSessionRefusal(err: unknown, queryKey: readonly unknown[]): boolean {
  return err instanceof ApiError && err.status === 401 && queryKey[0] !== 'identity';
}

export function createAppQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (err, query) => {
        if (isSessionRefusal(err, query.queryKey)) {
          void client.invalidateQueries({ queryKey: identityKeys.session });
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // A stored analytics result still being prepared (202) is asked for
        // again after the server's retryAfterMs; any other error once.
        retry: snapshotRetry(1),
        retryDelay: snapshotRetryDelay,
        refetchOnWindowFocus: false,
      },
    },
  });
  return client;
}

/**
 * Signed out (any path: Sign out, lock-screen sign-out, an expired session):
 * the service worker drops anything that is not a static build file. It
 * never stores user data in the first place (src/pwa/sw.js).
 */
function SignOutCachePurge() {
  const { state } = useSessionContext();
  const signedOut = state.kind === 'signed_out';
  useEffect(() => {
    if (signedOut) purgeOfflineCaches();
  }, [signedOut]);
  return null;
}

export function App() {
  const [queryClient] = useState(createAppQueryClient);
  const [router] = useState(() => buildRouter(shellModules, isPlaceholder));
  return (
    <QueryClientProvider client={queryClient}>
      <PrefsProvider>
        <PrivacyProvider>
          <TooltipProvider>
            <IdentitySessionProvider>
              <SignOutCachePurge />
              <RouterProvider router={router} />
            </IdentitySessionProvider>
          </TooltipProvider>
        </PrivacyProvider>
      </PrefsProvider>
    </QueryClientProvider>
  );
}
